import { useEffect, useMemo, useRef, useState } from 'react';
import ViewControls from '../scene/ViewControls';
import { useNavigate } from 'react-router-dom';
import RowMenu from '../../components/RowMenu';
import { KINDS } from '../../concepts/kinds';
import { isKnownKind, type ProgramView } from '../../programs/types';
import { AddProgramsMenu } from '../../pages/home/shared';
import { ProgramMenuItems } from '../../pages/home/ClassicHome';
import type { HomeModel } from '../../pages/home/useHome';
import { colourOfCategory, glyphOf, growthText, motionAllowed, useGrowth } from '../scene/programs';
import { useFollow, useScene } from '../scene/useScene';
import type { BlocksHandle, TowerSpec } from './Blocks';

function useWide() {
  const [wide, setWide] = useState(window.innerWidth > 720);
  useEffect(() => {
    const on = () => setWide(window.innerWidth > 720);
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  return wide;
}

export default function BlocksHome({ h }: { h: HomeModel }) {
  const nav = useNavigate();
  const [selected, setSelected] = useState<string | null>(null);
  const [hovered, setHovered] = useState<string | null>(null);
  const [focus, setFocus] = useState<number | null>(null);
  const wide = useWide();
  const moving = motionAllowed(h.settings.reduceMotion);
  const all = h.shown;
  const growth = useGrowth(all);

  const towers = useMemo(() => {
    const names = [...new Set(all.map((p) => p.category ?? 'Other'))].sort((a, b) => {
      const ia = h.categories.indexOf(a), ib = h.categories.indexOf(b);
      return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib) || a.localeCompare(b);
    });
    return names.map((name, i) => {
      const programs = all
        .filter((p) => (p.category ?? 'Other') === name)
        .sort((a, b) => Number(b.track === 'MAINTENANCE') - Number(a.track === 'MAINTENANCE') || a.name.localeCompare(b.name));
      return { name, colour: colourOfCategory(null, i), programs };
    });
  }, [all, h.categories]);

  const specs: TowerSpec[] = useMemo(() => towers.map((t) => ({
    name: t.name,
    colour: t.colour,
    blocks: t.programs.map((p) => {
      const g = growth.get(p.id);
      return { id: p.id, glyph: glyphOf(p), learned: p.track === 'MAINTENANCE', fill: g?.percent != null ? g.percent / 100 : 0 };
    }),
  })), [towers, growth]);

  const { canvasRef, handleRef, failed } = useScene<BlocksHandle>(
    'blocks',
    (canvas, cb) => import('./Blocks').then(({ mountBlocks }) => mountBlocks(canvas, moving, cb)),
    { onPick: (id) => { setFocus(null); setSelected(id); }, onHover: setHovered },
    (handle) => { handle.setTowers(specs); handle.setSelected(selected); handle.setFocus(focus); },
  );

  useEffect(() => { handleRef.current?.setTowers(specs); }, [specs]);
  useEffect(() => { handleRef.current?.setSelected(selected); }, [selected]);
  useEffect(() => { handleRef.current?.setFocus(focus); }, [focus]);
  useEffect(() => { handleRef.current?.setMoving(moving); }, [moving]);

  const cardRef = useRef<HTMLDivElement | null>(null);
  const tipRef = useRef<HTMLDivElement | null>(null);
  useFollow(cardRef, () => (selected ? handleRef.current?.locate(selected) ?? null : null), wide && !!selected && !failed);
  useFollow(tipRef, () => (hovered ? handleRef.current?.locate(hovered) ?? null : null), !!hovered && hovered !== selected && !failed);

  const chosen = all.find((p) => p.id === selected) ?? null;
  const tip = all.find((p) => p.id === hovered) ?? null;
  const learned = all.filter((p) => p.track === 'MAINTENANCE').length;
  const playUrl = (p: ProgramView) => `/play/${p.id}${h.learnerId ? `?learner=${encodeURIComponent(h.learnerId)}` : ''}`;
  const kindLabel = (p: ProgramView) => (isKnownKind(p.kind) ? KINDS[p.kind].label : p.kind);
  const fillOf = (p: ProgramView) => (p.track === 'MAINTENANCE' ? 100 : growth.get(p.id)?.percent ?? 0);

  return (
    <div className="blk">
      {!failed && <canvas ref={canvasRef} className="scene-canvas" aria-hidden="true" />}
      {!failed && <ViewControls handleRef={handleRef} />}

      <aside className="blk-panel" aria-label="Programs">
        <div className="blk-score">
          <span className="blk-score-big">{learned}<small>/{all.length}</small></span>
          <span className="blk-score-text">blocks solid<br />— learned programs</span>
        </div>
        {h.categories.length > 0 && (
          <select className="blk-select" aria-label="Category" value={h.categoryFilter} onChange={(e) => { setFocus(null); h.setCategoryFilter(e.target.value); }}>
            <option value="">Every tower</option>
            {h.categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        )}
          {h.kinds.length > 1 && (
            <select className="blk-select" aria-label="Type of game" value={h.kindFilter} onChange={(e) => { setFocus(null); h.setKindFilter(e.target.value); }}>
              <option value="">All types of game</option>
              {h.kinds.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
            </select>
          )}
        {h.error && <div className="error" role="alert">{h.error}</div>}
        {!h.loading && all.length === 0 && <p className="blk-empty">No blocks yet. Add a program and its tower starts here.</p>}
        <ol className="blk-towers">
          {towers.map((t, i) => (
            <li key={t.name}>
              <button
                type="button"
                className={focus === i ? 'blk-tower is-active' : 'blk-tower'}
                aria-pressed={focus === i}
                onClick={() => { setSelected(null); setFocus(focus === i ? null : i); }}
              >
                <span className="blk-tower-n" style={{ background: t.colour }}>{i + 1}</span>
                <span className="blk-tower-name">{t.name}</span>
                <span className="blk-tower-meta">{t.programs.filter((p) => p.track === 'MAINTENANCE').length}/{t.programs.length}</span>
              </button>
              <ul className="blk-rows">
                {t.programs.map((p) => (
                  <li key={p.id}>
                    <button
                      type="button"
                      className={selected === p.id ? 'blk-row is-active' : 'blk-row'}
                      aria-pressed={selected === p.id}
                      onClick={() => { setFocus(null); setSelected(p.id); }}
                    >
                      <span className="blk-row-name">{p.name}</span>
                      <span className="blk-row-bar" aria-hidden="true"><span style={{ width: `${fillOf(p)}%`, background: t.colour }} /></span>
                    </button>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ol>
        <div className="blk-actions">
          <AddProgramsMenu h={h} />
        </div>
      </aside>

      {tip && tip.id !== selected && (
        <div ref={tipRef} className="blk-tip" aria-hidden="true"><span>{tip.name}</span></div>
      )}

      {chosen && (
        <div ref={cardRef} className={wide ? 'blk-card is-floating' : 'blk-card'} role="region" aria-label={chosen.name}>
          <div className="blk-card-inner">
            <p className="blk-card-name">{chosen.name}</p>
            <p className="blk-card-meta">{chosen.category ?? 'Other'} · {kindLabel(chosen)}</p>
            <div className="blk-card-fill" aria-hidden="true"><span style={{ width: `${fillOf(chosen)}%` }} /></div>
            <p className="blk-card-meta">{growthText(growth.get(chosen.id))}{h.dueIds.has(chosen.id) ? ' · ready for a check' : ''}</p>
            <div className="blk-card-actions">
              <button type="button" className="btn" onClick={() => nav(playUrl(chosen))}>Play</button>
              <RowMenu label={`More for ${chosen.name}`}>
                <ProgramMenuItems h={h} p={chosen} />
              </RowMenu>
              <button type="button" className="btn secondary" onClick={() => setSelected(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
